import { NgxPermissionsService } from 'ngx-permissions'
import {
  SearchContactTypesRequest,
  SearchContactTitlesRequest,
  SearchOrganizationRequest,
  SearchIndustryRequest,
  FetchContactsRequest,
} from './../../../shared/shared.actions'
import {
  getSearchContactTypesState,
  getSearchContactTitlesState,
  getSearchOrganizationsState,
  getSearchIndustriesState,
  getContactsState,
} from './../../../shared/shared.select'
import {
  UpdateField,
  GeneratePropertyBusinessReportRequest,
} from './../properties.actions'
import {
  getPropertyState,
  getCreatePropertyState,
  isPropertyBusinessReportGeneratingState,
} from './../properties.selectors'
import { Map, List, fromJS } from 'immutable'
import { Router, ActivatedRoute } from '@angular/router'
import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core'
import { Store, select } from '@ngrx/store'
import FormTrait from 'src/app/traits/form.trait'
import { Subscription, Subject, Observable } from 'rxjs'
import { debounceTime } from 'rxjs/operators'

@Component({
  selector: 'app-property-contacts',
  templateUrl: './property-contacts.component.html',
  styleUrls: ['./property-contacts.component.css'],
})
export class PropertyContactsComponent
  extends FormTrait
  implements OnInit, OnDestroy
{
  public propertyId: number
  private state$: Subscription
  private searchContactTypesState$: Subscription
  private searchContactTitlesState$: Subscription
  public isLoading: boolean = false
  public isReadonly: boolean = false
  public contactTypes: List<Map<string, any>>
  public contactTitles: List<Map<string, any>>
  public onContactTypesAutocomplete$ = new Subject<string>()
  public onContactTitlesAutocomplete$ = new Subject<string>()
  private contactTypesAutocomplete$: Subscription
  private contactTitlesAutocomplete$: Subscription
  private searchOrganizationsState$: Subscription
  private searchIndustriesState$: Subscription
  public onRegistrantsAutocomplete$ = new Subject<string>()
  private registrantsAutocomplete$: Subscription
  public organizations: object[] = []
  public onIndustriesAutocomplete$ = new Subject<string>()
  private industriesAutocomplete$: Subscription
  public industries: object[] = []
  public separatorKeys = [',', ' ']
  public nextStatus: string
  private parent: string
  public hasReadonlyPermission: boolean = false
  public isPropertyBusinessReportGenerating: Observable<boolean>
  public contacts: any
  public isAddContactOpen = false
  public existingContacts: any[] = []
  public contacts$: Subscription
  public contactAutocomplete$: Subscription
  public onContactAutocomplete$ = new Subject<string>()
  public selectedContact: any

  constructor(
    public permissionsService: NgxPermissionsService,
    private store: Store<any>,
    public router: Router,
    private route: ActivatedRoute,
    private cd: ChangeDetectorRef,
  ) {
    super()
  }

  ngOnInit() {
    this.propertyId = parseInt(this.route.parent.snapshot.params.id, 10)
    this.hasReadonlyPermission =
      this.permissionsService.getPermissions()['READONLY'] !== undefined
    this.parent = this.propertyId ? 'property' : 'createProperty'
    const getState = this.propertyId ? getPropertyState : getCreatePropertyState
    this.isPropertyBusinessReportGenerating = this.store.pipe(
      select(isPropertyBusinessReportGeneratingState),
    )
    this.state$ = this.store.pipe(select(getState)).subscribe((state) => {
      this.state = state
      this.isLoading = state.get('isLoading')

      if (state.getIn(['validationErrors']).size) {
        this.cd.reattach()
      }

      if (this.propertyId) {
        this.isReadonly =
          (this.state.getIn(['data', 'status', 'name']) !== 'DATA_ENTRY' &&
            this.state.getIn(['data', 'status', 'name']) !== 'OBJECTION') ||
          this.hasReadonlyPermission ||
          this.state.getIn(['data', 'status', 'name']) === 'ARCHIVED'
      }

      if (this.getField(['contacts'])) {
        this.contacts = this.getField(['contacts']).map((contact) => {
          if (contact.get('business')) {
            return contact.setIn(
              ['business', 'isProportion'],
              contact.getIn(['business', 'businessArea']) > 0 ||
                contact.getIn(['business', 'isProportion']) === 0
                ? 0
                : 1,
            )
          }

          return contact
        })
      }
    })

    this.searchContactTypesState$ = this.store
      .pipe(select(getSearchContactTypesState))
      .subscribe((state) => {
        this.contactTypes = state.get('data').toJS()
      })

    this.searchContactTitlesState$ = this.store
      .pipe(select(getSearchContactTitlesState))
      .subscribe((state) => {
        this.contactTitles = state.get('data').toJS()
      })

    this.contactTypesAutocomplete$ = this.onContactTypesAutocomplete$
      .pipe(debounceTime(500))
      .subscribe((keyword) => {
        if (keyword) {
          this.store.dispatch<any>(new SearchContactTypesRequest(keyword))
        }
      })

    this.contactTitlesAutocomplete$ = this.onContactTitlesAutocomplete$
      .pipe(debounceTime(500))
      .subscribe((keyword) => {
        if (keyword) {
          this.store.dispatch<any>(new SearchContactTitlesRequest(keyword))
        }
      })

    this.searchOrganizationsState$ = this.store
      .pipe(select(getSearchOrganizationsState))
      .subscribe((state) => {
        this.organizations = state.get('data').toJS()
      })

    this.searchIndustriesState$ = this.store
      .pipe(select(getSearchIndustriesState))
      .subscribe((state) => {
        this.industries = state.get('data').toJS()
      })

    this.registrantsAutocomplete$ = this.onRegistrantsAutocomplete$
      .pipe(debounceTime(500))
      .subscribe((keyword) => {
        if (keyword) {
          this.store.dispatch<any>(new SearchOrganizationRequest(keyword))
        }
      })

    this.industriesAutocomplete$ = this.onIndustriesAutocomplete$
      .pipe(debounceTime(500))
      .subscribe((keyword) => {
        if (keyword) {
          this.store.dispatch<any>(new SearchIndustryRequest(keyword))
        }
      })

    this.contacts$ = this.store
      .pipe(select(getContactsState))
      .subscribe((state) => {
        this.existingContacts = state.get('data').toJS()
      })

    this.contactAutocomplete$ = this.onContactAutocomplete$
      .pipe(debounceTime(500))
      .subscribe((keyword = '') => {
        this.store.dispatch<any>(
          new FetchContactsRequest({
            filters: {
              or: [
                { field: 'fullName', value: keyword ?? '' },
                { field: 'taxId', value: keyword ?? '' },
              ],
            },
            pageSize: 50,
          }),
        )
      })

    this.store.dispatch<any>(new SearchOrganizationRequest(''))
    this.store.dispatch<any>(new SearchContactTypesRequest(''))
    this.store.dispatch<any>(new SearchContactTitlesRequest(''))

    this.store.dispatch<any>(
      new FetchContactsRequest({
        filters: {},
        pageSize: 20,
      }),
    )
  }

  proportionChange(isProportion, path) {
    this.cd.reattach()

    if (isProportion) {
      this.store.dispatch(
        new UpdateField(
          'businessArea',
          0,
          `${this.parent}.data.contacts.${path.join('.')}`,
        ),
      )
    } else {
      this.store.dispatch(
        new UpdateField(
          'businessProportion',
          0,
          `${this.parent}.data.contacts.${path.join('.')}`,
        ),
      )
    }

    this.store.dispatch(
      new UpdateField(
        'isProportion',
        isProportion,
        `${this.parent}.data.contacts.${path.join('.')}`,
      ),
    )
  }

  public switch(e, path) {
    const name = e.target.name
    const checked = e.target.checked

    setTimeout(() => {
      this.store.dispatch(
        new UpdateField(
          name,
          checked,
          `${this.parent}.data.contacts.${path.join('.')}`,
        ),
      )

      this.getField(['contacts']).forEach((_, index) => {
        if (path[0] != index) {
          this.store.dispatch(
            new UpdateField(
              name,
              false,
              `${this.parent}.data.contacts.${[index].join('.')}`,
            ),
          )
        }
      })
    }, 500)
  }

  public reportBusiness(id: number) {
    this.store.dispatch(new GeneratePropertyBusinessReportRequest(id))
  }

  public getSubIndustries(id: number) {
    const subIndustries: any = this.industries.filter((i: any) => i.id == id)
    return subIndustries.length ? subIndustries[0].subIndustryList : []
  }

  public addBusiness(contactIndex: number) {
    const value = this.getField(['contacts']).setIn(
      [contactIndex, 'business'],
      fromJS({
        businessName: null,
        typeOfBusiness: null,
        businessTin: null,
        businessProportion: 0,
        lessThenQuoter: null,
        businessArea: 0,
        isProportion: 1,
        subIndustry: {
          industry: null,
        },
        registrants: [],
      }),
    )

    this.store.dispatch(
      new UpdateField('contacts', value, `${this.parent}.data`),
    )
    this.cd.reattach()
  }

  public removeBusiness(contactIndex: number) {
    const value = (this.getField(['contacts']) as any)
      .setIn([contactIndex, 'business'], null)
      .toJS()
    this.store.dispatch(
      new UpdateField('contacts', value, `${this.parent}.data`),
    )
    this.cd.reattach()
  }

  public onRowAdd(contact?: any) {
    const value = (
      (this.getField(['contacts']) as any[]).unshift({
        contactType: null,
        contact: contact || {
          lastName: null,
          adultNumber: null,
          taxId: null,
          tenantNumber: null,
          contactTitle: null,
          firstName: null,
          phoneNumbers: [],
          email: null,
          contactComment: null,
        },
        contactTypeOther: null,
      }) as any
    ).toJS()
    this.store.dispatch(
      new UpdateField('contacts', value, `${this.parent}.data`),
    )
    this.cd.reattach()
    this.selectedContact = null
  }
  public onDelete(index) {
    const value = (this.getField(['contacts']) as any).delete(index).toJS()
    this.store.dispatch(
      new UpdateField('contacts', value, `${this.parent}.data`),
    )
    this.cd.reattach()
  }

  public onAdd(e: any, path: [], name: null | string = null) {
    const current = (this.getField(['contacts', ...path, name]) as any).toJS()
    e = [...current, e.value]

    this.store.dispatch(
      new UpdateField(
        name || e.target.name,
        name ? e : e.target.value,
        `${this.parent}.data.contacts.${path.join('.')}`,
      ),
    )

    this.store.dispatch(
      new UpdateField(
        'validationErrors',
        this.state.get('validationErrors').delete(name || e.target.name),
        this.parent,
      ),
    )
    this.cd.reattach()
  }

  public onRemove(e: any, path: [], name: null | string = null) {
    const current = this.getField(['contacts', ...path, name]) as any

    var i = current.findIndex((item) => item === e)

    e = [...current.delete(i).toJS()]

    this.store.dispatch(
      new UpdateField(
        name || e.target.name,
        name ? e : e.target.value,
        `${this.parent}.data.contacts.${path.join('.')}`,
      ),
    )

    this.store.dispatch(
      new UpdateField(
        'validationErrors',
        this.state.get('validationErrors').delete(name || e.target.name),
        this.parent,
      ),
    )
  }

  public handleFieldChange(e: any, path: [], name: null | string = null): void {
    this.cd.detach()

    if (name) {
      this.cd.reattach()
    }

    if (name === 'industry') {
      const businessPath = path
      businessPath.pop()

      this.store.dispatch(
        new UpdateField(
          'subIndustry',
          {},
          `${this.parent}.data.contacts.${businessPath.join('.')}`,
        ),
      )

      this.store.dispatch(
        new UpdateField(
          name || e.target.name,
          name ? e : e.target.value,
          `${this.parent}.data.contacts.${[...businessPath, 'subIndustry'].join(
            '.',
          )}`,
        ),
      )
    } else {
      this.store.dispatch(
        new UpdateField(
          name || e.target.name,
          name ? e : e.target.value,
          `${this.parent}.data.contacts.${path.join('.')}`,
        ),
      )
    }

    this.store.dispatch(
      new UpdateField(
        'validationErrors',
        this.state
          .get('validationErrors')
          .delete(`contacts.${path.join('.')}.${name || e.target.name}`),
        this.parent,
      ),
    )
  }

  ngOnDestroy() {
    this.state$.unsubscribe()
    this.searchContactTypesState$.unsubscribe()
    this.searchContactTitlesState$.unsubscribe()
    this.contactTitlesAutocomplete$.unsubscribe()
    this.contactTypesAutocomplete$.unsubscribe()
    this.contactAutocomplete$.unsubscribe()
  }
}
